import {
  memo,
  FunctionComponent,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react';
import { OpenCase as OpenCaseType } from '~types';
import _ from 'lodash';

import { useGameCount, useSliderStyle, useStatusGame } from '../hooks';
import { GetName } from '~frontend/components/item';

interface SliderItemProps {
  getGame: OpenCaseType;
}

const SliderItem: FunctionComponent<SliderItemProps> = ({
  getGame: {
    getGame: { getItem: wonItem },
    getItems,
  },
}) => {
  const [gameStatus] = useStatusGame();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const prizeRef = useRef<HTMLDivElement>(null);
  const [prizeItemSize, setPrizeItemSize] = useState(0);
  const [wripperSize, setWripperSize] = useState(0);
  const [countOpen] = useGameCount();
  const [sliderProps] = useSliderStyle();
  const getPrizeOffset = (
    itemSize: number,
    prizeIndex: number,
    containerCenter: number,
  ) => {
    return itemSize * prizeIndex - (containerCenter - itemSize / 2);
  };

  const setSize = () => {
    setPrizeItemSize(
      countOpen > 1
        ? prizeRef?.current.offsetHeight
        : prizeRef?.current.offsetWidth,
    );
    setWripperSize(
      countOpen > 1
        ? wrapperRef?.current.offsetHeight
        : wrapperRef?.current.offsetWidth,
    );
  };

  useEffect(() => {
    if (!prizeRef?.current) {
      return;
    }

    setSize();

    window.addEventListener('resize', setSize);

    return () => {
      window.removeEventListener('resize', setSize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef]);

  const getRandomIntInRange = (min: number, max: number): number => {
    const minNumber = Math.ceil(min);
    const maxNumber = Math.floor(max);

    return Math.floor(Math.random() * (maxNumber - minNumber + 1) + minNumber);
  };

  const getPrizeAdditionalOffset = useCallback((itemSize: number): number => {
    const center = itemSize / 3;

    const randomOffset = getRandomIntInRange(-center + 1, center - 1);

    return randomOffset;
  }, []);

  const prizeIndexOffset = useMemo(() => {
    let prizeOffset = getPrizeOffset(
      prizeItemSize,
      countOpen > 1 ? 77 : 72,
      wripperSize / 2,
    );

    if (countOpen === 1) {
      if (prizeItemSize === 120) {
        prizeOffset = prizeOffset + prizeItemSize / 2.5;
      } else {
        prizeOffset = prizeOffset - prizeItemSize / 2.5;
      }
    }

    const additionalOffset = getPrizeAdditionalOffset(prizeItemSize);

    if (gameStatus === 3) {
      return prizeOffset;
    }

    return prizeOffset + additionalOffset;
  }, [
    countOpen,
    gameStatus,
    getPrizeAdditionalOffset,
    prizeItemSize,
    wripperSize,
  ]);

  const sliderStyle = useMemo(() => {
    if (!sliderProps) {
      return {
        transform: 'unset',
      };
    }

    const { timeout } = sliderProps;

    return {
      transform:
        countOpen > 1
          ? `translate3d(0px, -${prizeIndexOffset}px, 0px)`
          : `translate3d(-${prizeIndexOffset}px, 0px, 0px)`,
      transition: `${
        gameStatus === 3 ? 2500 : timeout
      }ms cubic-bezier(0.32, 0.64, 0.45, 1)`,
    };
  }, [countOpen, prizeIndexOffset, sliderProps, gameStatus]);

  return (
    <div className="case-play-slider-wrap" style={sliderStyle} ref={wrapperRef}>
      {_.map(getItems, (getItem, index: number) => (
        <div
          className={`case-slider-item ${
            gameStatus === 3 && (index === 70 ? 'show' : 'hide')
          }`}
          key={`${getItem.id}-${index}`}
          data-win={index === 70}
          ref={prizeRef}
        >
          <div
            className="in-case-item status-4"
            data-rar={index === 70 ? wonItem.color : getItem.color}
            data-audio="5.wav"
            data-volume="0.4"
          >
            <div className="in-case-item-wrap">
              <div className="in-case-item-bg" />
              <div className="img">
                <img
                  src={`${
                    index === 70 ? wonItem.getImage : getItem.getImage
                  }/350fx350f`}
                  alt={index === 70 ? wonItem.getName : getItem.getName}
                />
              </div>
              {gameStatus === 3 && index === 70 && (
                <div className="in-case-info">
                  <div className="in-case-item-name">
                    <GetName getItem={wonItem} type={0} />
                  </div>
                  <div className="in-case-item-desc">
                    <GetName getItem={wonItem} type={1} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default memo(SliderItem);
